import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Flex, Radio, Select } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { Dayjs } from 'dayjs';
import * as React from 'react';
import { SetStateAction, useCallback, useMemo } from 'react';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { cleanArray } from '../utils';
import { MONTH_VIEW, MonthIndex, ViewType, YEAR_VIEW } from './types';
import translations from './CalendarHeader.translations.json';
import { getCurrentDecadeYears, getMonthNames } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory('app');
addTranslations(translations);

export function CalendarHeader(props: {
  period: Dayjs;
  onChangePeriod: (period: SetStateAction<Dayjs>) => void;
  onChangeType?: (type: ViewType) => void;
  type: ViewType;
  id: string;
}) {
  const { period, onChangePeriod, onChangeType, type, id } = props;
  const { t } = useTranslation();
  const typeOptions = useMemo(
    () => [
      { label: t('field.type.month'), value: MONTH_VIEW },
      { label: t('field.type.year'), value: YEAR_VIEW },
    ],
    [t],
  );
  const year = period.year();
  const monthOptions = useMemo(
    () =>
      getMonthNames().map((label, value) => ({
        label,
        value,
      })),
    [],
  );
  const yearOptions = useMemo(
    () =>
      getCurrentDecadeYears(year).map((value) => ({
        label: value.toString(),
        value,
      })),
    [year],
  );
  const changeMonth = useCallback(
    (month: MonthIndex) => onChangePeriod((period) => period.month(month)),
    [onChangePeriod],
  );
  const changeYear = useCallback(
    (year: number) => onChangePeriod((period) => period.year(year)),
    [onChangePeriod],
  );
  const changeView = useCallback(
    ({ target: { value } }: RadioChangeEvent) => {
      onChangeType?.(value);
    },
    [onChangeType],
  );
  const nextPeriod = useCallback(
    () => onChangePeriod((state) => state.add(1, type)),
    [onChangePeriod, type],
  );
  const prevPeriod = useCallback(
    () => onChangePeriod((state) => state.add(-1, type)),
    [onChangePeriod, type],
  );

  return (
    <HeaderWrapper justify="space-between">
      <Button
        id={[id, 'prevPeriod'].join('-')}
        icon={<Icon.ChevronLeft size={19} />}
        onClick={prevPeriod}
      />
      <SelectionWrapper className="calendar-header-selection-wrapper">
        <Flex gap={12} justify="end">
          <Select
            id={cleanArray([id, 'year']).join('-')}
            value={year}
            options={yearOptions}
            onChange={changeYear}
          />

          {props.type === MONTH_VIEW && (
            <Select
              id={[id, 'month'].join('-')}
              className="month-select"
              value={period.month() as MonthIndex}
              options={monthOptions}
              onChange={changeMonth}
            />
          )}
          {onChangeType && (
            <div>
              <Radio.Group
                optionType="button"
                options={typeOptions}
                onChange={changeView}
                value={type}
              />
            </div>
          )}
        </Flex>
      </SelectionWrapper>
      <Button
        id={[id, 'nextPeriod'].join('-')}
        icon={<Icon.ChevronRight size={19} />}
        className="end"
        onClick={nextPeriod}
      />
    </HeaderWrapper>
  );
}

const HeaderWrapper = styled(Flex)`
  margin-right: 1px;
`;

const SelectionWrapper = styled.div`
  margin: 0 12px 12px 12px;
  width: 532px;

  .ant-divider {
    margin: 18px 0;
  }
  .month-select {
    width: 160px;
  }
`;
